import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { toast } from 'react-toastify';
import { apiBlob } from '../../../api/api';
import history from '../../../history';
import SearchDidYouMean from './SearchDidYouMean';
import { setIndeices } from '../../../context/sessionStorage';
import { getPathList } from '../Tracks/LessonItem/utils';

const SearchResults = ({
    search,
    showResult,
    filters,
    indices,
    classRooms,
    user,
    fetchStudentInClassRooms,
    onSetFileFavorite,
    onSetLessonFavorite,
    onShowAll,
    searchValue,
    onSelectSuggestedKey,
    activeTab,
    searchTime
}) => {
    const { t } = useTranslation('common');

    const renderLoading = () => (
        <div className="bg-white rounded-lg p-5 text-center shadow-sm" style={{ borderRadius: '24px' }}>
            <div className="mb-3 text-primary" style={{ fontSize: '2.8rem' }}>
                <i className="fas fa-spinner fa-spin"></i>
            </div>
            <h5 className="text-dark font-weight-bold">
                {t("Pages.Search.Loading", "جارٍ البحث عن النتائج المطابقة...")}
            </h5>
            <p className="text-muted small mb-0">يرجى الانتظار لحظات بسيطة</p>
        </div>
    );

    // Exact download handler matching Files/ListItem download action
    const handleFileDownload = (item) => {
        if (user && user.user) {
            let _toastId = toast(t('Processing', 'جارٍ تنفيذ الطلب، الرجاء الانتظار'), {
                position: "bottom-left",
                autoClose: false
            });
            toast.update(_toastId, {
                render: () => <div> <i className="fa-spin fas fa-spinner ml-1"></i> {t('Processing', 'جارٍ تنفيذ الطلب، الرجاء الانتظار')} </div>,
                autoClose: false,
                closeOnClick: false,
            });

            apiBlob.post(`/EducationalFiles/?token=${item.token}`).then(response => {
                if (response) {
                    var a = document.createElement("a");
                    document.body.appendChild(a);
                    a.style.display = "none";
                    const blob = new Blob([response.data]),
                        url = window.URL.createObjectURL(blob);
                    a.href = url;
                    a.download = item.Name + (item.extension ? '.' + item.extension : '');
                    a.click();
                    window.URL.revokeObjectURL(url);
                }
                toast.dismiss(_toastId);
            }).catch(error => {
                toast.dismiss(_toastId);
                history.push("/NoPermissions");
            });
        } else {
            history.push("/Login");
        }
    };

    // Render full index path breadcrumb with clickable Links based on category type
    const renderIndexPath = (item, category) => {
        if (!item || !item.Index || !item.Index.Id) {
            return (
                <span className="badge badge-light text-dark px-2 py-1 small">
                    {item.DomainId ? `مجال ${item.DomainId}` : '-'}
                </span>
            );
        }

        const activeNodes = indices ? (indices.nodes || indices) : [];
        const pathList = getPathList(activeNodes, item.Index.Id, 1);
        const itemType = (item.Index && item.Index.Type) ? item.Index.Type : category;

        let routePrefix = "Lessons";
        if (itemType === "Files") routePrefix = "Files";
        else if (itemType === "Exams") routePrefix = "Exams";
        else if (itemType === "Quizzes") routePrefix = "Quizzes";
        else if (itemType === "Games" || itemType === "Fun") routePrefix = "Games";
        else if (itemType === "Books") routePrefix = "Books";

        if (pathList && pathList.length > 0) {
            const topParent = pathList[0];
            const length = pathList.length;

            return (
                <div className="index-path-breadcrumb d-flex align-items-center flex-wrap" style={{ fontSize: '0.82rem' }}>
                    {pathList.map((node, index) => {
                        const targetUrl = node.id === topParent.id
                            ? `/${routePrefix}/${topParent.id}`
                            : `/${routePrefix}/${topParent.id}/${node.id}`;

                        return (
                            <span key={node.id} className="d-inline-flex align-items-center my-1">
                                <Link
                                    to={targetUrl}
                                    className="badge badge-light text-dark font-weight-normal px-2 py-1 border text-decoration-none transition-all"
                                    style={{ borderRadius: '6px', backgroundColor: '#f8fafc', borderColor: '#e2e8f0' }}
                                    title={`الانتقال إلى ${node.label}`}
                                >
                                    {node.label}
                                </Link>
                                {length !== index + 1 && <i className="fas fa-arrow-left text-info mx-1 small" style={{ fontSize: '0.65rem' }}></i>}
                            </span>
                        );
                    })}
                </div>
            );
        }

        const singleUrl = `/${routePrefix}/${item.Index.Id}`;

        return (
            <Link
                to={singleUrl}
                className="badge badge-light text-dark font-weight-normal px-2 py-1 border text-decoration-none transition-all"
                style={{ borderRadius: '6px', backgroundColor: '#f8fafc', borderColor: '#e2e8f0' }}
            >
                {item.Index.Name || '-'}
            </Link>
        );
    };

    // Get specific action details, label and URL route per category type
    const getItemActionDetails = (category, item) => {
        const itemType = (item.Index && item.Index.Type) ? item.Index.Type : category;

        if (itemType === "Exams") {
            return {
                label: "عرض الامتحان",
                url: `/ExamView/${item.Id}`,
                icon: "fas fa-file-signature ml-1",
                btnStyle: { backgroundColor: '#dc3545', borderColor: '#dc3545' },
                btnClass: "btn-danger text-white"
            };
        } else if (itemType === "Quizzes") {
            return {
                label: "اختبر نفسك",
                url: `/ExamView/${item.Id}`,
                icon: "fas fa-question-circle ml-1",
                btnStyle: { backgroundColor: '#ffc107', borderColor: '#ffc107', color: '#212529' },
                btnClass: "btn-warning text-dark"
            };
        } else if (itemType === "Games" || itemType === "Fun") {
            return {
                label: "العب الآن",
                url: `/GameView/${item.Id}`,
                icon: "fas fa-gamepad ml-1",
                btnStyle: { backgroundColor: '#28a745', borderColor: '#28a745' },
                btnClass: "btn-success text-white"
            };
        } else if (itemType === "Books") {
            return {
                label: "قراءة الكتاب",
                url: `/LessonView/${item.Id}`,
                icon: "fas fa-book-bookmark ml-1",
                btnStyle: { backgroundColor: '#6f42c1', borderColor: '#6f42c1' },
                btnClass: "btn-purple text-white"
            };
        } else if (itemType === "SelfDevelopment") {
            return {
                label: "تطوير الذات",
                url: `/SelfDevelopment/${item.Id}`,
                icon: "fas fa-seedling ml-1",
                btnStyle: { backgroundColor: '#17a2b8', borderColor: '#17a2b8' },
                btnClass: "btn-info text-white"
            };
        } else {
            // Default Lessons
            return {
                label: "عرض الدرس",
                url: `/LessonView/${item.Id}`,
                icon: "fas fa-book-open ml-1",
                btnStyle: { backgroundColor: '#007bff', borderColor: '#007bff' },
                btnClass: "btn-primary text-white"
            };
        }
    };

    if (!showResult) return null;
    if (search.isLoaded === false) return renderLoading();

    if (indices) setIndeices(indices);

    // Combine all active filtered results into 1 master list
    const getCombinedResults = () => {
        let combined = [];

        if (filters.IsLessons && search.Lessons) {
            search.Lessons.forEach(item => combined.push({ category: 'Lessons', label: t('PageHeader.Lessons', 'درس'), icon: 'fas fa-book-open', color: '#007bff', item }));
        }
        if (filters.IsFiles && search.Files) {
            search.Files.forEach(item => combined.push({ category: 'Files', label: t('PageHeader.Files', 'ملف'), icon: 'fas fa-paperclip', color: '#17a2b8', item }));
        }
        if (filters.IsExams && search.Exams) {
            search.Exams.forEach(item => combined.push({ category: 'Exams', label: t('PageHeader.Exams', 'امتحان'), icon: 'fas fa-file-signature', color: '#dc3545', item }));
        }
        if (filters.IsQuizzes && search.Quizzes) {
            search.Quizzes.forEach(item => combined.push({ category: 'Quizzes', label: t('PageHeader.Quizzes', 'اختبار'), icon: 'fas fa-question-circle', color: '#ffc107', item }));
        }
        if (filters.IsFun && search.Games) {
            search.Games.forEach(item => combined.push({ category: 'Games', label: t('PageHeader.Fun', 'لعبة'), icon: 'fas fa-gamepad', color: '#28a745', item }));
        }
        if (filters.IsBooks && search.Books) {
            search.Books.forEach(item => combined.push({ category: 'Books', label: t('PageHeader.Books', 'كتاب'), icon: 'fas fa-book-bookmark', color: '#6f42c1', item }));
        }
        if (filters.IsSkills && search.Skills) {
            search.Skills.forEach(item => combined.push({ category: 'Skills', label: t('PageHeader.Skills', 'مهارة'), icon: 'fas fa-lightbulb', color: '#fd7e14', item }));
        }

        if (activeTab && activeTab !== 'all') {
            combined = combined.filter(x => x.category === activeTab);
        }

        return combined;
    };

    const combinedResults = getCombinedResults();
    const isCompletelyEmpty = search.isLoaded && combinedResults.length === 0 && !search.suggestedKey;

    return (
        <div className="w-100">
            <SearchDidYouMean
                suggestedKey={search.suggestedKey}
                onSelectKey={onSelectSuggestedKey}
            />

            {isCompletelyEmpty && (
                <div
                    className="p-5 text-center bg-white shadow-sm mb-4"
                    style={{ borderRadius: '24px' }}
                >
                    <div
                        className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                        style={{ width: '80px', height: '80px', backgroundColor: '#fff3cd', color: '#ffc107', fontSize: '36px' }}
                    >
                        <i className="fas fa-search-minus"></i>
                    </div>
                    <h4 className="font-weight-bold text-dark mb-2">
                        {t("Pages.Search.NotFound", "عفواً، لم نجد نتائج")} "{searchValue}"
                    </h4>
                    <p className="text-muted max-w-500 mx-auto mb-4" style={{ maxWidth: '500px' }}>
                        جرّب إدخال مرادفات أخرى للكلمة، أو تأكد من اختيار الفئات المطلوبة في التصفية أعلاه.
                    </p>
                </div>
            )}

            {!isCompletelyEmpty && (
                <div
                    className="mb-4 bg-white shadow-sm border-0 transition-all position-relative"
                    style={{
                        zIndex: 1,
                        borderRadius: '24px',
                        boxShadow: '0 12px 35px rgba(0,0,0,0.06)',
                        border: '1px solid #edf2f7',
                        overflow: 'hidden'
                    }}
                >
                    {/* Modern Data Table Header */}
                    <div
                        className="p-3 px-4 d-flex align-items-center justify-content-between border-bottom"
                        style={{ backgroundColor: '#ffffff' }}
                    >
                        <div className="d-flex align-items-center">
                            <div
                                className="rounded-circle d-flex align-items-center justify-content-center ml-3"
                                style={{
                                    width: '42px',
                                    height: '42px',
                                    background: 'linear-gradient(135deg, #92278f 0%, #6b1b69 100%)',
                                    color: '#ffffff',
                                    fontSize: '18px',
                                    boxShadow: '0 4px 12px rgba(146, 39, 143, 0.3)'
                                }}
                            >
                                <i className="fas fa-table-list"></i>
                            </div>
                            <div>
                                <h5 className="title mb-0 font-weight-bold text-dark" style={{ fontSize: '1.2rem' }}>
                                    {t("Pages.Search.MasterTableTitle", "نتائج البحث")}
                                </h5>
                                <small className="text-muted font-weight-bold d-block mt-1" style={{ fontSize: '0.85rem' }}>
                                    {t("Pages.Search.TotalResults", "إجمالي النتائج")}: <span className="text-primary font-weight-bold">{combinedResults.length}</span> {t("Pages.Search.ResultsCount", "نتيجة")}
                                </small>
                            </div>
                        </div>

                        <div className="d-flex align-items-center flex-wrap">
                            <span
                                className="badge badge-pill px-3 py-2 font-weight-bold ml-2 mb-1"
                                style={{
                                    backgroundColor: '#92278f15',
                                    color: '#92278f',
                                    fontSize: '0.9rem'
                                }}
                            >
                                {t("Pages.Search.TotalResults", "إجمالي النتائج")}: {combinedResults.length} {t("Pages.Search.ResultsCount", "نتيجة")}
                            </span>

                            {searchTime && (
                                <span
                                    className="badge badge-pill px-3 py-2 font-weight-bold mb-1"
                                    style={{
                                        backgroundColor: '#17a2b815',
                                        color: '#17a2b8',
                                        fontSize: '0.85rem'
                                    }}
                                >
                                    <i className="fas fa-clock ml-1"></i>
                                    {t("Pages.Search.SearchTime", "زمن البحث")}: {searchTime} {t("Pages.Search.SearchTimeSeconds", "ثانية")}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Modern Data Table Content */}
                    <div className="table-responsive">
                        <table className="table table-hover border-0 mb-0 text-right align-middle">
                            <thead className="text-muted font-weight-bold small" style={{ backgroundColor: '#f8fafc' }}>
                                <tr>
                                    <th style={{ width: '50px', borderTop: 'none', textAlign: 'center' }}>{t("Pages.Search.TableColNum", "#")}</th>
                                    <th style={{ borderTop: 'none' }}>{t("Pages.Search.TableColTitle", "العنوان والفهرس والوصف")}</th>
                                    <th style={{ width: '70px', borderTop: 'none', textAlign: 'center' }}>{t("Pages.Search.TableColFavorite", "المفضلة")}</th>
                                    <th style={{ width: '160px', borderTop: 'none', textAlign: 'center' }}>{t("Pages.Search.TableColAction", "الإجراءات والمعاينة")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {combinedResults.map((row, index) => {
                                    const actionDetails = getItemActionDetails(row.category, row.item);
                                    const iconUrl = row.item.Icon || row.item.Image || row.item.Picture;

                                    return (
                                        <tr key={`${row.category}-${row.item.Id}`} className="transition-all hover-row">
                                            <td className="text-center font-weight-bold text-muted small align-middle">
                                                {index + 1}
                                            </td>
                                            <td className="align-middle">
                                                <div className="d-flex align-items-start py-1">
                                                    {/* Exact Thumbnail Image rendering as in /Tracks/LessonItem */}
                                                    <div
                                                        className="product-thumb rounded-lg d-flex align-items-center justify-content-center flex-shrink-0 ml-3 overflow-hidden shadow-sm position-relative mt-1"
                                                        style={{
                                                            width: '56px',
                                                            height: '56px',
                                                            borderRadius: '12px',
                                                            backgroundColor: '#ffffff',
                                                            border: '1px solid #e2e8f0',
                                                            padding: '2px'
                                                        }}
                                                    >
                                                        <LazyLoadImage
                                                            src={iconUrl || "/assets/images/logo/130p150.png"}
                                                            alt={row.item.Name || ''}
                                                            className="w-100 h-100"
                                                            style={{ objectFit: 'contain' }}
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.src = "/assets/images/logo/130p150.png";
                                                            }}
                                                        />
                                                    </div>

                                                    {/* Details: Title, Index Path underneath, Bio */}
                                                    <div className="flex-grow-1">
                                                        {row.category === "Files" ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleFileDownload(row.item)}
                                                                className="btn btn-link p-0 font-weight-bold text-dark h6 mb-1 text-right text-decoration-none transition-all hover-title w-auto border-0 shadow-none d-block"
                                                                style={{ fontSize: '1.02rem' }}
                                                            >
                                                                {row.item.Name}
                                                                {row.item.extension && (
                                                                    <span className="badge badge-light text-muted mr-2 uppercase" style={{ fontSize: '0.75rem' }}>
                                                                        .{row.item.extension}
                                                                    </span>
                                                                )}
                                                            </button>
                                                        ) : row.category === "Skills" ? (
                                                            <span className="font-weight-bold text-dark h6 mb-1 d-block" style={{ fontSize: '1.02rem' }}>
                                                                {row.item.Name}
                                                            </span>
                                                        ) : (
                                                            <Link
                                                                to={actionDetails.url}
                                                                className="font-weight-bold text-dark h6 mb-1 d-block text-decoration-none transition-all hover-title"
                                                                style={{ fontSize: '1.02rem' }}
                                                            >
                                                                {row.item.Name}
                                                            </Link>
                                                        )}

                                                        {/* Index Path Breadcrumb DIRECTLY UNDER TITLE */}
                                                        <div className="mb-1">
                                                            {renderIndexPath(row.item, row.category)}
                                                        </div>

                                                        {/* Bio / Description */}
                                                        {row.item.Bio && (
                                                            <small className="text-muted d-block text-truncate" style={{ maxWidth: '550px' }}>
                                                                {row.item.Bio}
                                                            </small>
                                                        )}

                                                        {/* Skill Tags */}
                                                        {row.category === "Skills" && (
                                                            <div className="d-flex flex-wrap align-items-center mt-1">
                                                                {row.item.TextTypeName && <span className="badge badge-light text-primary px-2 py-1 mr-1 small">{row.item.TextTypeName}</span>}
                                                                {row.item.DimensionComponentName && <span className="badge badge-light text-secondary px-2 py-1 small">{row.item.DimensionComponentName}</span>}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="align-middle text-center">
                                                {row.category === "Files" && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0 text-danger shadow-none w-auto"
                                                        onClick={() => onSetFileFavorite && onSetFileFavorite(row.item.Id)}
                                                        title="المفضلة"
                                                    >
                                                        <i className={`${row.item.isFavorite ? 'fas' : 'far'} fa-heart font-size-h5`}></i>
                                                    </button>
                                                )}
                                                {row.category !== "Files" && row.category !== "Skills" && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0 text-danger shadow-none w-auto"
                                                        onClick={() => onSetLessonFavorite && onSetLessonFavorite(row.item.Id)}
                                                        title="المفضلة"
                                                    >
                                                        <i className={`${row.item.isFavorite ? 'fas' : 'far'} fa-heart font-size-h5`}></i>
                                                    </button>
                                                )}
                                                {row.category === "Skills" && (
                                                    <span className="text-muted small">-</span>
                                                )}
                                            </td>
                                            <td className="align-middle text-center">
                                                {row.category === "Files" ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleFileDownload(row.item)}
                                                        className="btn btn-sm btn-outline-info rounded-pill px-3 py-1 font-weight-bold shadow-sm transition-all w-auto"
                                                    >
                                                        <i className="fas fa-download ml-1"></i> {t("Pages.Search.DownloadOrOpen", "تحميل / فتح")}
                                                    </button>
                                                ) : row.category === "Skills" ? (
                                                    <span className="badge badge-pill badge-light text-warning px-3 py-2 border border-warning w-auto">
                                                        <i className="fas fa-check-circle ml-1"></i> مهارة
                                                    </span>
                                                ) : (
                                                    <Link
                                                        to={actionDetails.url}
                                                        className={`btn btn-sm rounded-pill px-3 py-1 font-weight-bold shadow-sm transition-all w-auto ${actionDetails.btnClass}`}
                                                        style={actionDetails.btnStyle}
                                                    >
                                                        <i className={`${actionDetails.icon}`}></i> {actionDetails.label}
                                                    </Link>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchResults;
